/**
 * Public Privacy Policy page (/shop/privacy).
 *
 * TCGMA-180 — the Terms of Service (§10) links here, so the page must exist and
 * be publicly reachable.
 *
 * IMPORTANT — THIS IS A DRAFT, NOT LEGAL ADVICE. It's a reasonable starting
 * point for a trading-card resale storefront + trade-show booth, but it MUST be
 * reviewed by a lawyer and customized before it's treated as legally operative.
 * Fill in every [BRACKETED] placeholder (legal business name, contact email,
 * payment processor, retention periods, jurisdiction-specific rights, children's
 * age threshold). Edit the SECTIONS array below or swap the body wholesale.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

export const Route = createFileRoute("/shop/privacy")({
  component: ShopPrivacyPage,
});

const LAST_UPDATED = "August 2026";

const SECTIONS: { heading: string; body: string }[] = [
  {
    heading: "1. Introduction",
    body: "This Privacy Policy explains how [LEGAL BUSINESS NAME] (“[SHORT NAME],” “we,” “us,” or “our”) collects, uses, and shares personal information when you visit our storefront at [thenooblounge.com], sign up at our trade-show booths, enter a raffle, or place an order. By using our services you agree to this Policy. It is incorporated into our Terms of Service.",
  },
  {
    heading: "2. Information we collect",
    body: "Information you give us: your name, email address, and (if provided) phone number when you sign up at a booth, enter a raffle, or create an account; your shipping address and order details when you buy; your card interests; and whether you opt in to marketing. Information collected automatically: basic device and usage data such as IP address, browser type, and the pages you view, gathered through standard web logs and cookies. We do not collect more than we need to run the store and follow up with you.",
  },
  {
    heading: "3. How we use your information",
    body: "We use your information to: create and secure your account and send passwordless sign-in links; process, fulfill, and support your orders; run trade-show signups and free-entry raffles; send transactional messages (sign-in links, order updates); send marketing or newsletter email only if you opted in; prevent fraud and abuse and keep our services secure; understand and improve our services; and comply with legal obligations.",
  },
  {
    heading: "4. Cookies and similar technologies",
    body: "We use cookies and similar technologies to keep you signed in, remember your cart, and understand how the site is used. You can control cookies through your browser settings, though some features may not work properly without them. [If you use analytics or advertising cookies, name the providers here and how to opt out.]",
  },
  {
    heading: "5. Email and marketing choices",
    body: "Transactional emails (for example, sign-in links and order updates) are necessary to provide the service and are sent regardless of marketing preferences. Marketing or newsletter email is sent only to people who opt in, and you can unsubscribe at any time using the link in those emails or by contacting us.",
  },
  {
    heading: "6. How we share information",
    body: "We share personal information only as needed to run our business: with service providers that act on our behalf — for example, our email delivery provider, [PAYMENT PROCESSOR], shipping carriers, and hosting/infrastructure providers — under obligations to protect it; when required by law or to protect our rights, safety, or property; and in connection with a business transfer such as a merger or sale of assets. We do NOT sell your personal information. [Confirm and adjust this statement for your jurisdiction.]",
  },
  {
    heading: "7. Payment information",
    body: "Payments are processed by [PAYMENT PROCESSOR]. We do not store full payment card numbers on our systems; that information is handled by the processor under its own terms and security standards.",
  },
  {
    heading: "8. Data retention",
    body: "We keep personal information for as long as needed to provide our services, comply with legal, tax, and accounting obligations, resolve disputes, and enforce our agreements. Lead and raffle records are kept while they remain useful for follow-up and then archived or deleted. [State specific retention periods where you can.]",
  },
  {
    heading: "9. Security",
    body: "We use reasonable administrative and technical safeguards to protect personal information — including passwordless sign-in and limiting who can access customer data. No method of transmission or storage is completely secure, however, so we cannot guarantee absolute security. Please help by keeping access to your email account secure.",
  },
  {
    heading: "10. Your rights and choices",
    body: "Depending on where you live, you may have rights to access, correct, delete, or receive a copy of your personal information, to opt out of marketing, and to object to or restrict certain processing. To exercise these rights, contact us at [privacy@thenooblounge.com]; we may need to verify your identity first. [Add jurisdiction-specific disclosures as applicable — for example, California (CCPA/CPRA) or the EU/UK (GDPR).]",
  },
  {
    heading: "11. Children's privacy",
    body: "Our services are not directed to children under [13], and we do not knowingly collect personal information from them. If you believe a child has given us personal information, please contact us and we will delete it.",
  },
  {
    heading: "12. Third-party links",
    body: "Our services may link to third-party sites we do not control. This Policy does not apply to those sites, and we are not responsible for their privacy practices. Please review the privacy policies of any site you visit.",
  },
  {
    heading: "13. Changes to this Policy",
    body: "We may update this Policy from time to time. When we do, we will revise the “last updated” date above. Material changes take effect when posted, and your continued use of the services after that means you accept the updated Policy.",
  },
  {
    heading: "14. Contact us",
    body: "Questions or requests about your privacy? Contact us at [privacy@thenooblounge.com] or the address listed on our storefront.",
  },
];

function ShopPrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to shop
      </Link>

      <h1 className="text-2xl font-semibold text-neutral-900">
        Privacy Policy
      </h1>
      <p className="mt-1 text-sm text-neutral-500">
        Last updated: {LAST_UPDATED}
      </p>
      <p className="mt-4 text-sm leading-relaxed text-neutral-700">
        This Policy explains what we collect, how we use it, and the choices you
        have. It applies to our online storefront and our trade-show booths.
      </p>

      <div className="mt-8 space-y-6">
        {SECTIONS.map((s) => (
          <section key={s.heading}>
            <h2 className="text-base font-semibold text-neutral-900">
              {s.heading}
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-700">
              {s.body}
            </p>
          </section>
        ))}
      </div>
    </div>
  );
}
